import { TextField, TextFieldProps } from '@mui/material';
import { useId, useMemo } from 'react';
import {
  FieldPath,
  FieldValues,
  useController,
  UseControllerProps,
  UseControllerReturn,
  ValidationRule,
  ValidationValue,
} from 'react-hook-form';

export const getRuleValue = <T extends ValidationValue>(rule?: ValidationRule<T>): T | undefined =>
  !rule || typeof rule === 'string' || typeof rule === 'number' || typeof rule === 'boolean' || rule instanceof RegExp ? rule : rule.value;

export type TextFieldControlProps<T extends FieldValues, TName extends FieldPath<T> = FieldPath<T>> = UseControllerProps<T, TName> &
  TextFieldProps & {
    /**
     * Custom resolver to control the helper text rendering.
     *
     * If provided, this function fully overrides the default helper text behavior.
     * That means no fallback to `fieldState.error?.message` or `props.helperText` is applied.
     * The resolver is responsible for handling all cases (e.g. validation errors, empty state, etc.).
     *
     * @param ctx - Context containing the current field state and validation rules
     * @param ctx.fieldState - React Hook Form field state (includes error, invalid, etc.)
     * @param ctx.rules - Validation rules passed to the controller
     * @returns The string to display as helper text, or `undefined` to render nothing
     *
     * @example
     * ```tsx
     * <TextFieldControl
     *   name="age"
     *   control={control}
     *   rules={{ min: 18, required: true }}
     *   helperTextResolver={({ fieldState, rules }) => {
     *     if (!fieldState.error) return undefined;
     *
     *     switch (fieldState.error.type) {
     *       case 'required':
     *         return 'This field is required';
     *       case 'min':
     *         return `Minimum value is ${rules?.min}`;
     *       default:
     *         return 'Invalid value';
     *     }
     *   }}
     * />
     * ```
     */
    helperTextResolver?: (ctx: {
      fieldState: UseControllerReturn<T, TName>['fieldState'];
      rules?: UseControllerProps<T, TName>['rules'];
    }) => string | undefined;
  };

export const TextFieldControl = <T extends FieldValues, TName extends FieldPath<T> = FieldPath<T>>({
  name,
  rules,
  control,
  defaultValue,
  shouldUnregister,
  ...props
}: TextFieldControlProps<T, TName>): React.JSX.Element => {
  const { field, fieldState } = useController<T, TName>({ name, rules, control, defaultValue, shouldUnregister });
  const id = useId();

  const htmlInputProps = useMemo(() => {
    return {
      maxLength: getRuleValue(rules?.maxLength),
      min: getRuleValue(rules?.min),
      max: getRuleValue(rules?.max),
      ...props.slotProps?.htmlInput,
    };
  }, [props.slotProps?.htmlInput, rules?.maxLength, rules?.min, rules?.max]);

  return (
    <TextField
      {...field}
      {...props}
      id={id}
      required={props.required || !!rules?.required}
      slotProps={{
        ...props.slotProps,
        inputLabel: {
          shrink: true,
          ...props.slotProps?.inputLabel,
        },
        htmlInput: htmlInputProps,
      }}
      error={fieldState.invalid}
      helperText={
        props.helperTextResolver ? props.helperTextResolver({ fieldState, rules }) : fieldState.error?.message || props.helperText
      }
    />
  );
};
